/**
 * =============================================================================
 * LightingSystem - Dynamic lighting with darkness overlay and light sources
 * =============================================================================
 * 
 * Provides atmospheric lighting effects using:
 * - Darkness overlay that covers the world based on time of day
 * - Light sources (torches, campfires, entities) that illuminate the darkness
 * - Smooth radial gradients for soft light edges
 * 
 * =============================================================================
 */

import Phaser from 'phaser';

/**
 * Configuration for a light source
 */
export interface LightSource {
  x: number;
  y: number;
  radius: number;
  intensity: number;
  color: number;
  flicker?: boolean;
  flickerOffset?: number;
  baseIntensity?: number;
  /**
   * Pulso lento tipo "heartbeat" (lub-dub) en vez del flicker rápido de antorcha.
   * Usado por los ítems únicos en el piso: la luz respira con calma. Excluyente
   * con `flicker` (si ambos están, gana el flicker en el cómputo de intensidad).
   */
  pulse?: boolean;
  pulseOffset?: number;
}

/**
 * Configuration for light sources in assets-index.json
 */
export interface LightConfig {
  radius: number;
  intensity: number;
  color: string;
  flicker?: boolean;
}

export class LightingSystem {
  private scene: Phaser.Scene;
  private ambientDarkness: number = 0;
  private lightSources: Map<string, LightSource> = new Map();
  /** Fuentes de OSCURIDAD: gradientes radiales que oscurecen localmente (sombra suave). */
  private darkSources: Map<string, LightSource> = new Map();

  /**
   * Efecto "interior de casa": cuando el player está dentro de un área `interior`, se
   * oscurece TODO alrededor y se recortan estos rects (en world px) para dejar el interior
   * iluminado. `interiorIntensity` (0..1) lo maneja/eased GameScene para un fade sutil.
   */
  private interiorRects: Array<{ x: number; y: number; w: number; h: number }> = [];
  private interiorIntensity: number = 0;
  private static readonly MAX_INTERIOR_ALPHA = 0.62;
  /** Radio del difuminado del borde del recorte (px). Grande → bordes suaves (no cuadrados). */
  private static readonly INTERIOR_FEATHER = 14;
  
  private lightCanvas: HTMLCanvasElement;
  private lightContext: CanvasRenderingContext2D;
  private overlayImage: Phaser.GameObjects.Image | null = null;
  private overlayTexture: Phaser.Textures.CanvasTexture | null = null;
  
  private static readonly DARKNESS_COLOR = { r: 0, g: 0, b: 0 };
  private static readonly MAX_DARKNESS_ALPHA = 0.85;
  private static readonly OVERLAY_DEPTH = 799;
  private static readonly TEXTURE_KEY = 'lighting_overlay';
  
  private flickerTime: number = 0;
  private static readonly FLICKER_SPEED = 8;
  private static readonly FLICKER_AMOUNT = 0.15;
  /**
   * Pulso "heartbeat": período del latido (s). Debe mantenerse IGUAL a `HEARTBEAT_PERIOD_S` en
   * `GroundItemsController` para que la luz real y el glow del ítem único laten en fase.
   */
  private static readonly PULSE_PERIOD = 3.6;
  /** Profundidad del guiño: la luz baja a (1 − PULSE_DEPTH)× en reposo y sube a 1× en el golpe. */
  private static readonly PULSE_DEPTH = 0.4;
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    
    const cam = scene.cameras.main;
    const width = cam.width > 0 ? cam.width : scene.scale.width;
    const height = cam.height > 0 ? cam.height : scene.scale.height;
    
    this.lightCanvas = document.createElement('canvas');
    this.lightCanvas.width = width;
    this.lightCanvas.height = height;
    this.lightContext = this.lightCanvas.getContext('2d')!;
    
    if (scene.textures.exists(LightingSystem.TEXTURE_KEY)) {
      scene.textures.remove(LightingSystem.TEXTURE_KEY);
    }
    this.overlayTexture = scene.textures.createCanvas(
      LightingSystem.TEXTURE_KEY,
      width,
      height
    );
    
    this.overlayImage = scene.add.image(width / 2, height / 2, LightingSystem.TEXTURE_KEY);
    this.overlayImage.setDepth(LightingSystem.OVERLAY_DEPTH);
    this.overlayImage.setScrollFactor(0);
  }
  
  setAmbientDarkness(level: number): void {
    this.ambientDarkness = Math.max(0, Math.min(1, level));
  }
  
  getAmbientDarkness(): number {
    return this.ambientDarkness;
  }
  
  addLightSource(id: string, source: LightSource): void {
    if (source.flicker) {
      source.flickerOffset = Math.random() * Math.PI * 2;
      source.baseIntensity = source.intensity;
    } else if (source.pulse) {
      // Offset 0 por default (determinista): así el latido de la luz queda EN FASE con el glow
      // dibujado a mano del ítem único, que usa el mismo reloj (scene.time.now) y período.
      source.pulseOffset = source.pulseOffset ?? 0;
      source.baseIntensity = source.intensity;
    }
    this.lightSources.set(id, source);
  }
  
  removeLightSource(id: string): void {
    this.lightSources.delete(id);
  }

  /**
   * Agrega/actualiza una fuente de OSCURIDAD (sombra suave). A diferencia de una
   * luz, oscurece localmente con un gradiente radial → bordes suaves (sin el
   * "cuadriculado" de tiles). Funciona incluso de día (ambientDarkness=0).
   */
  addDarkSource(id: string, source: LightSource): void {
    this.darkSources.set(id, source);
  }

  removeDarkSource(id: string): void {
    this.darkSources.delete(id);
  }

  /**
   * Setea el efecto de interior de casa: `rectsWorld` = rects a recortar (world px),
   * `intensity` = 0..1 (0 desactiva). GameScene lo llama cada frame con el valor eased.
   */
  setInterior(
    rectsWorld: Array<{ x: number; y: number; w: number; h: number }>,
    intensity: number
  ): void {
    this.interiorRects = rectsWorld;
    this.interiorIntensity = Math.max(0, Math.min(1, intensity));
  }
  
  updateLightPosition(id: string, x: number, y: number): void {
    const light = this.lightSources.get(id);
    if (light) {
      light.x = x;
      light.y = y;
    }
  }
  
  hasLightSource(id: string): boolean {
    return this.lightSources.has(id);
  }
  
  clearLightSources(): void {
    this.lightSources.clear();
  }
  
  /**
   * Clear decoration and map light sources (preserves entity lights).
   * Called when reloading the map.
   */
  clearDecorationLights(): void {
    const keysToDelete: string[] = [];
    for (const id of this.lightSources.keys()) {
      // Clear decoration lights (deco_) and map lights (map_)
      if (id.startsWith('deco_') || id.startsWith('map_')) {
        keysToDelete.push(id);
      }
    }
    for (const key of keysToDelete) {
      this.lightSources.delete(key);
    }
    // Idem para fuentes de oscuridad de decoración/mapa.
    const darkToDelete: string[] = [];
    for (const id of this.darkSources.keys()) {
      if (id.startsWith('deco_') || id.startsWith('map_')) darkToDelete.push(id);
    }
    for (const key of darkToDelete) this.darkSources.delete(key);
  }
  
  update(delta: number = 16): void {
    if (!this.overlayImage || !this.overlayTexture) return;
    
    this.flickerTime += delta / 1000;
    
    const cam = this.scene.cameras.main;
    const ctx = this.lightContext;
    
    if (this.lightCanvas.width !== cam.width || this.lightCanvas.height !== cam.height) {
      this.lightCanvas.width = cam.width;
      this.lightCanvas.height = cam.height;
      
      if (this.scene.textures.exists(LightingSystem.TEXTURE_KEY)) {
        this.scene.textures.remove(LightingSystem.TEXTURE_KEY);
      }
      this.overlayTexture = this.scene.textures.createCanvas(
        LightingSystem.TEXTURE_KEY,
        cam.width,
        cam.height
      );
      this.overlayImage.setTexture(LightingSystem.TEXTURE_KEY);
      this.overlayImage.setPosition(cam.width / 2, cam.height / 2);
    }
    
    // Renderizar el overlay si hay oscuridad ambiente O fuentes de oscuridad
    // locales (sombras de sector) O el efecto de interior de casa, aunque sea de día.
    const hasDark = this.darkSources.size > 0;
    const hasInterior = this.interiorIntensity > 0.01 && this.interiorRects.length > 0;
    if (this.ambientDarkness <= 0.01 && !hasDark && !hasInterior) {
      this.overlayImage.setVisible(false);
      return;
    }

    this.overlayImage.setVisible(true);

    const darknessAlpha = this.ambientDarkness * LightingSystem.MAX_DARKNESS_ALPHA;

    ctx.clearRect(0, 0, this.lightCanvas.width, this.lightCanvas.height);

    if (this.ambientDarkness > 0.01) {
      const { r, g, b } = LightingSystem.DARKNESS_COLOR;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${darknessAlpha})`;
      ctx.fillRect(0, 0, this.lightCanvas.width, this.lightCanvas.height);

      ctx.globalCompositeOperation = 'destination-out';
      for (const [, light] of this.lightSources) {
        this.drawLight(light, cam, ctx);
      }
      ctx.globalCompositeOperation = 'source-over';

      // Second pass: draw colored light overlays using 'lighter' (additive) blend
      ctx.globalCompositeOperation = 'lighter';
      for (const [, light] of this.lightSources) {
        this.drawLightColor(light, cam, ctx, darknessAlpha);
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    // Fuentes de oscuridad locales: gradiente radial oscuro (sombra suave). Se
    // dibujan DESPUÉS de las luces para que oscurezcan aunque haya luz cerca.
    for (const [, dark] of this.darkSources) {
      this.drawDarkSource(dark, cam, ctx);
    }

    // Efecto de interior de casa: oscurece TODO y recorta los rects del interior
    // (borde difuminado) → interior iluminado, afuera más oscuro. Se dibuja al final
    // para que el recorte borre también la oscuridad ambiente sobre el interior.
    if (hasInterior) {
      this.drawInterior(cam, ctx);
    }

    const textureCanvas = this.overlayTexture.getSourceImage() as HTMLCanvasElement;
    const textureCtx = textureCanvas.getContext('2d')!;
    textureCtx.clearRect(0, 0, textureCanvas.width, textureCanvas.height);
    textureCtx.drawImage(this.lightCanvas, 0, 0);
    this.overlayTexture.refresh();
    
    this.overlayImage.setPosition(cam.width / 2, cam.height / 2);
    this.overlayImage.setDisplaySize(cam.width, cam.height);
  }
  
  /**
   * Intensidad efectiva de una luz este frame, modulada por su modo de animación:
   * `flicker` (parpadeo rápido de antorcha, suma de senos) o `pulse` (heartbeat lento
   * lub-dub con reposo). Sin modo → intensidad fija. Factoriza la cuenta que antes
   * estaba duplicada en `drawLight` y `drawLightColor`.
   */
  private effectiveIntensity(light: LightSource): number {
    if (light.flicker && light.baseIntensity !== undefined && light.flickerOffset !== undefined) {
      const flicker1 = Math.sin(this.flickerTime * LightingSystem.FLICKER_SPEED + light.flickerOffset);
      const flicker2 = Math.sin(this.flickerTime * LightingSystem.FLICKER_SPEED * 2.3 + light.flickerOffset * 1.7) * 0.5;
      const flickerValue = (flicker1 + flicker2) / 1.5;
      return light.baseIntensity * (1 + flickerValue * LightingSystem.FLICKER_AMOUNT);
    }
    if (light.pulse && light.baseIntensity !== undefined) {
      // Reloj de escena (mismo que el glow del ítem único) → luz y glow laten en fase.
      const clock = this.scene.time.now / 1000;
      const offsetNorm = (light.pulseOffset ?? 0) / (Math.PI * 2);
      let phase = ((clock / LightingSystem.PULSE_PERIOD) + offsetNorm) % 1;
      if (phase < 0) phase += 1;
      // Doble golpe (lub-dub): dos bumps gaussianos cercanos + reposo el resto del ciclo.
      const dist0 = Math.min(phase, 1 - phase); // distancia circular alrededor de 0 (1er golpe)
      const bump0 = Math.exp(-(dist0 * dist0) / (2 * 0.05 * 0.05));
      const d1 = phase - 0.16; // 2do golpe, apenas después
      const bump1 = 0.6 * Math.exp(-(d1 * d1) / (2 * 0.045 * 0.045));
      const wave = Math.min(1, bump0 + bump1); // 0 = reposo, 1 = pico del latido
      // Mismo guiño que los glints del ítem (curva wave²): la luz queda casi plana en reposo y
      // sube en cada golpe → ACOMPAÑA al destello (mismo reloj/período/fase → suben juntos).
      const g = wave * wave;
      return light.baseIntensity * (1 - LightingSystem.PULSE_DEPTH + LightingSystem.PULSE_DEPTH * g);
    }
    return light.intensity;
  }

  /**
   * Draw the colored tint of a light source.
   * This is drawn after the darkness is "punched through" to add color.
   */
  private drawLightColor(
    light: LightSource,
    cam: Phaser.Cameras.Scene2D.Camera,
    ctx: CanvasRenderingContext2D,
    darknessAlpha: number
  ): void {
    const screenX = light.x - cam.scrollX;
    const screenY = light.y - cam.scrollY;
    
    const margin = light.radius * 1.5;
    if (screenX < -margin || screenX > cam.width + margin ||
        screenY < -margin || screenY > cam.height + margin) {
      return;
    }
    
    // Extract RGB from the light color
    const r = (light.color >> 16) & 0xFF;
    const g = (light.color >> 8) & 0xFF;
    const b = light.color & 0xFF;
    
    // Skip if the light is white or near-white (no color tint needed)
    if (r > 240 && g > 240 && b > 240) {
      return;
    }

    const intensity = this.effectiveIntensity(light);

    // Color intensity is subtle - we don't want to overwhelm the scene
    // Scale by darkness level so color is more visible at night
    const colorAlpha = Math.min(0.15, intensity * 0.1) * darknessAlpha;
    
    const gradient = ctx.createRadialGradient(
      screenX, screenY, 0,
      screenX, screenY, light.radius * 0.7 // Color radius is smaller than light radius
    );
    
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${colorAlpha})`);
    gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${colorAlpha * 0.5})`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(screenX, screenY, light.radius * 0.7, 0, Math.PI * 2);
    ctx.fill();
  }
  
  /**
   * Dibuja una fuente de OSCURIDAD: gradiente radial oscuro con falloff suave
   * (bordes difuminados). `intensity` = opacidad máxima en el centro (0..1);
   * `color` opcional tiñe la sombra (default negro). Se dibuja con 'source-over'.
   */
  private drawDarkSource(
    dark: LightSource,
    cam: Phaser.Cameras.Scene2D.Camera,
    ctx: CanvasRenderingContext2D
  ): void {
    const screenX = dark.x - cam.scrollX;
    const screenY = dark.y - cam.scrollY;

    const margin = dark.radius * 1.5;
    if (screenX < -margin || screenX > cam.width + margin ||
        screenY < -margin || screenY > cam.height + margin) {
      return;
    }

    const r = (dark.color >> 16) & 0xff;
    const g = (dark.color >> 8) & 0xff;
    const b = dark.color & 0xff;
    const a = Math.min(LightingSystem.MAX_DARKNESS_ALPHA, Math.max(0, dark.intensity));

    const gradient = ctx.createRadialGradient(
      screenX, screenY, 0,
      screenX, screenY, dark.radius
    );
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${a})`);
    gradient.addColorStop(0.45, `rgba(${r}, ${g}, ${b}, ${a * 0.7})`);
    gradient.addColorStop(0.75, `rgba(${r}, ${g}, ${b}, ${a * 0.3})`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(screenX, screenY, dark.radius, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Dibuja el efecto de interior de casa: rellena toda la pantalla con oscuridad y
   * luego RECORTA (destination-out) los rects del interior con borde difuminado, así el
   * interior queda claro y todo alrededor más oscuro. Trabaja de día o de noche.
   */
  private drawInterior(
    cam: Phaser.Cameras.Scene2D.Camera,
    ctx: CanvasRenderingContext2D
  ): void {
    const alpha = this.interiorIntensity * LightingSystem.MAX_INTERIOR_ALPHA;
    ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
    ctx.fillRect(0, 0, this.lightCanvas.width, this.lightCanvas.height);

    // Recorte del interior: rectángulo CUADRADO (esquinas apenas redondeadas) que llega al
    // BORDE de la casa, con un borde difuminado suave (shadowBlur). El interior debe cubrir
    // toda la casa, no un círculo chico en el medio → inset ~0 y radio de esquina chico.
    const feather = LightingSystem.INTERIOR_FEATHER;
    // Sin inset: el recorte cubre el rect COMPLETO y el difuminado (feather) se extiende un
    // poco hacia afuera → la luz llega a los bordes de la casa (paredes) en vez de quedar
    // corta adentro. El rect ya es la caja de la casa, así que el desborde es mínimo.
    const inset = 0;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.shadowColor = 'rgba(0, 0, 0, 1)';
    ctx.shadowBlur = feather;
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    for (const r of this.interiorRects) {
      const sx = r.x - cam.scrollX;
      const sy = r.y - cam.scrollY;
      const ix = sx + inset;
      const iy = sy + inset;
      const iw = Math.max(1, r.w - inset * 2);
      const ih = Math.max(1, r.h - inset * 2);
      // Esquina apenas redondeada (cuadrado), no un óvalo: ~1/4 de tile, capado.
      const radius = Math.min(8, Math.min(iw, ih) * 0.12);
      ctx.beginPath();
      ctx.roundRect(ix, iy, iw, ih, radius);
      ctx.fill();
    }
    ctx.restore();
    ctx.globalCompositeOperation = 'source-over';
  }

  private drawLight(
    light: LightSource,
    cam: Phaser.Cameras.Scene2D.Camera,
    ctx: CanvasRenderingContext2D
  ): void {
    const screenX = light.x - cam.scrollX;
    const screenY = light.y - cam.scrollY;
    
    const margin = light.radius * 1.5;
    if (screenX < -margin || screenX > cam.width + margin ||
        screenY < -margin || screenY > cam.height + margin) {
      return;
    }
    
    const intensity = this.effectiveIntensity(light);

    // Create very smooth radial gradient for soft light falloff
    const gradient = ctx.createRadialGradient(
      screenX, screenY, 0,
      screenX, screenY, light.radius
    );
    
    // Extra soft gradient with more stops for smoother falloff
    const alpha = Math.min(1, intensity);
    gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
    gradient.addColorStop(0.1, `rgba(255, 255, 255, ${alpha * 0.95})`);
    gradient.addColorStop(0.2, `rgba(255, 255, 255, ${alpha * 0.85})`);
    gradient.addColorStop(0.3, `rgba(255, 255, 255, ${alpha * 0.7})`);
    gradient.addColorStop(0.4, `rgba(255, 255, 255, ${alpha * 0.55})`);
    gradient.addColorStop(0.5, `rgba(255, 255, 255, ${alpha * 0.4})`);
    gradient.addColorStop(0.6, `rgba(255, 255, 255, ${alpha * 0.28})`);
    gradient.addColorStop(0.7, `rgba(255, 255, 255, ${alpha * 0.18})`);
    gradient.addColorStop(0.8, `rgba(255, 255, 255, ${alpha * 0.1})`);
    gradient.addColorStop(0.9, `rgba(255, 255, 255, ${alpha * 0.04})`);
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(screenX, screenY, light.radius, 0, Math.PI * 2);
    ctx.fill();
  }
  
  destroy(): void {
    if (this.overlayImage) {
      this.overlayImage.destroy();
      this.overlayImage = null;
    }
    if (this.overlayTexture && this.scene.textures.exists(LightingSystem.TEXTURE_KEY)) {
      this.scene.textures.remove(LightingSystem.TEXTURE_KEY);
      this.overlayTexture = null;
    }
    this.lightSources.clear();
    this.darkSources.clear();
  }
  
  getLightSourceCount(): number {
    return this.lightSources.size;
  }
  
  getLightSourceIds(): string[] {
    return Array.from(this.lightSources.keys());
  }
}
